import { initials } from './ui';

// Faces for the team: people are round with initials, agents are rounded
// squares with their role icon. The ring shows the lane tone; the status is
// always also given in words next to the face, so the ring is never the only cue.

export type Tone = 'attn' | 'stuck' | 'work' | 'trial' | 'idle';

// First path of each icon is drawn in the highlight colour (two-tone).
const ROLE: Record<string, string[]> = {
  'reply-drafter': ['M9 14L4 9l5-5', 'M4 9h10a6 6 0 0 1 6 6v3'],
  'merge-reviewer': ['M3 12a6 6 0 1 0 12 0a6 6 0 1 0-12 0', 'M9 12a6 6 0 1 0 12 0a6 6 0 1 0-12 0'],
  'lead-intake': ['M3 4h18', 'M3 4l7 8v8l4-2v-6l7-8'],
  'sequence-runner': ['M22 2L11 13', 'M22 2l-7 20-4-9-9-4z'],
  'deal-progression': ['M14 7h7v7', 'M3 17l6-6 4 4 8-8'],
  'quote-drafter': ['M14 2v6h6', 'M6 2h9l5 5v15H6z', 'M9 13h6M9 17h4'],
  'campaign-planner': ['M17 8a5 5 0 0 1 0 8', 'M3 10v4h4l7 5V5L7 10z'],
  'speed-to-lead': ['M13 2L4 14h7', 'M11 14l-1 8 9-12h-7l1-8'],
  'content-drafter': ['M12 20h9', 'M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z'],
  'segment-suggester': ['M12 3v9h9', 'M21 12A9 9 0 1 1 12 3'],
  'cleanup-assistant': ['M7 21h13', 'M3.5 14.5l9-9a2 2 0 0 1 3 0l3 3a2 2 0 0 1 0 3l-8 8H7z', 'M9 9l6 6'],
  'migration-agent': ['M12 3v12', 'M7 10l5 5 5-5', 'M4 21h16'],
  'workspace-designer': ['M3 9h18M9 9v12', 'M3 3h18v18H3z'],
  'workflow-designer': ['M10 7h3a3 3 0 0 1 3 3v4', 'M4 4h6v6H4zM14 14h6v6h-6z'],
  'dashboard-designer': ['M10 20V4', 'M4 20V10M16 20v-7', 'M3 20h18'],
  'autonomy-reviewer': ['M9 12l2 2 4-4', 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z'],
  'benchmark-advisor': ['M11 12a1 1 0 1 0 2 0a1 1 0 1 0-2 0', 'M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0', 'M7 12a5 5 0 1 0 10 0a5 5 0 1 0-10 0'],
  'agent-builder': ['M15 3h6v6h-6z', 'M3 3h8v8H3zM3 13h8v8H3zM13 13h8v8h-8z'],
  'browser-agent': ['M3 9h18', 'M3 4h18v16H3z'],
  'connections': ['M9 7V3M15 7V3', 'M6 7h12v5a6 6 0 0 1-12 0zM12 18v3'],
  'churn-watch': ['M16 17h6v-6', 'M22 17l-8.5-8.5-5 5L2 7'],
  'coach': ['M22 10v6', 'M2 10l10-5 10 5-10 5z', 'M6 12v5c3 3 9 3 12 0v-5'],
  'day-planner': ['M9 15l2 2 4-4', 'M3 5h18v16H3zM3 10h18M8 3v4M16 3v4'],
  'fraud-watch': ['M12 9v4M12 17h.01', 'M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z'],
  'help-agent': ['M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01', 'M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0'],
  'inbox-agent': ['M22 12h-6l-2 3h-4l-2-3H2', 'M5.5 5.1L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.5-6.9A2 2 0 0 0 16.8 4H7.2a2 2 0 0 0-1.7 1.1z'],
  'okr-coach': ['M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z', 'M4 22v-7'],
  'onboarding-interviewer': ['M8 10h.01M12 10h.01M16 10h.01', 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'],
  'renewal-watch': ['M21 3v5h-5', 'M21 12a9 9 0 1 1-9-9c2.5 0 4.9 1 6.7 2.7L21 8'],
  'showing-follow-up': ['M9 22V12h6v10', 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'],
  'site-editor': ['M2 12h20M12 2a15 15 0 0 1 0 20a15 15 0 0 1 0-20', 'M2 12a10 10 0 1 0 20 0a10 10 0 1 0-20 0'],
  'spec-steward': ['M8 7h8M8 11h6', 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20V2H6.5A2.5 2.5 0 0 0 4 4.5v15z', 'M6.5 17A2.5 2.5 0 0 0 4 19.5 2.5 2.5 0 0 0 6.5 22H20v-5'],
  'sphere-keeper': ['M16 3.1a4 4 0 0 1 0 7.8M22 21v-2a4 4 0 0 0-3-3.9', 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M5 7a4 4 0 1 0 8 0a4 4 0 1 0-8 0'],
  'stakeholder-mapper': ['M12 9v3M12 12l-6 5M12 12l6 5', 'M9 3h6v6H9zM3 17h6v4H3zM15 17h6v4h-6z'],
};
const FALLBACK = ['M12 3v4', 'M4 7h16v12H4z', 'M9 12.5h.01M15 12.5h.01M9.5 16h5'];

// "reply-drafter" or "reply-drafter@default.2" -> "reply-drafter"
export const roleOf = (templateOrId: string | null | undefined) => (templateOrId ?? '').split('/').pop()!.split('@')[0]!;

export function RoleIcon({ template, size = 22 }: { template: string | null | undefined; size?: number }) {
  const paths = ROLE[roleOf(template)] ?? FALLBACK;
  return (
    <svg className="role-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths.map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
}

export function Face({ kind, name, template, tone, size = 46 }: { kind: 'person' | 'agent'; name: string; template?: string | null; tone?: Tone; size?: number }) {
  return (
    <span className={`face ${kind}${tone ? ` ring-${tone}` : ''}`} style={{ width: size, height: size, fontSize: Math.round(size / 3.2) }} aria-hidden="true">
      {kind === 'agent' ? <RoleIcon template={template} size={Math.round(size * 0.48)} /> : initials(name)}
    </span>
  );
}
